import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {message} from "antd";
import {AxiosError} from "axios";
import {BankAccount} from "../domain/bank-account.entity";
import {
  BankValidationResult,
  PayoutCountry,
  PayoutBank,
  BankBranch,
  ValidateBankParams,
  ConnectBankParams,
  UpdateBankParams,
} from "../domain/bank-account.types";
import {createBankAccountRepository, BankAccountRepositoryV2} from "../data/repositories";
import {InitiateUpdateParams} from "../data/repositories/bank-account.repository";
import {
  isEmptyConnectedBankAccountMessage,
  SYNTHETIC_NO_CONNECTED_BANK_MESSAGE,
} from "../utils/empty-connected-bank-error";

// ==================== Legacy API Response Types ====================
// These types match the old API response structure for backwards compatibility

export type LegacyBankResponse = {
  id: number;
  userId: number;
  country_name: string;
  country_code: string;
  currency: string;
  bank_name: string;
  bank_code: string;
  account_number: string;
  account_name: string;
  status_id: string;
  statusName: string;
  branch_code: string;
  provider: string;
  createdAt: string;
  updatedAt: string;
  loginUrl?: string | null;
};

export type LegacyConnectedBankResponse = {
  bank: LegacyBankResponse;
};

export type LegacyValidateAccountResponse = {
  name: string;
};

export type LegacyConnectBankResponse = {
  bank: {
    userId: number;
    country_name: string;
    country_code: string;
    bank_name: string;
    bank_code: string;
    account_number: string;
    account_name: string;
    provider: string;
    id: number;
    createdAt: string;
    updatedAt: string;
  };
};

// ==================== Converters ====================

/**
 * Converts BankAccount entity to legacy API response format.
 */
const toLegacyBankResponse = (b: BankAccount): LegacyBankResponse => ({
  id: b.id,
  userId: b.userId,
  country_name: b.countryName,
  country_code: b.countryCode,
  currency: b.currency,
  bank_name: b.bankName,
  bank_code: b.bankCode,
  account_number: b.accountNumber,
  account_name: b.accountName,
  status_id: b.statusId,
  statusName: b.status,
  branch_code: b.branchCode || "",
  provider: b.provider,
  createdAt: b.createdAt,
  updatedAt: b.updatedAt,
  loginUrl: b.loginUrl,
});

// ==================== Query Keys ====================

export const BANK_ACCOUNT_QUERY_KEYS = {
  all: ["bank_accounts"] as const,
  connected: (uniqueId?: string) =>
    uniqueId
      ? ([...BANK_ACCOUNT_QUERY_KEYS.all, "connected", {uniqueId}] as const)
      : ([...BANK_ACCOUNT_QUERY_KEYS.all, "connected"] as const),
  validation: (accountNumber?: string) =>
    [...BANK_ACCOUNT_QUERY_KEYS.all, "validation", accountNumber] as const,
  countries: () => [...BANK_ACCOUNT_QUERY_KEYS.all, "countries"] as const,
  banks: (countryCode: string | null) =>
    [...BANK_ACCOUNT_QUERY_KEYS.all, "banks", countryCode] as const,
  branches: (bankId: number | null) =>
    [...BANK_ACCOUNT_QUERY_KEYS.all, "branches", bankId] as const,
};

// Legacy query keys for cache compatibility
export const LEGACY_QUERY_KEYS = {
  connectedBank: (uniqueId: string) => ["connected_bank", {uniqueId}] as const,
  validateAccountNumber: (accountNumber?: string) =>
    ["validate_account_number", accountNumber] as const,
  payoutCountries: () => ["payout_countries"] as const,
  payoutBanks: (countryCode: string | null) => ["payout_banks", countryCode] as const,
  payoutBankBranches: (bankId: number | null) =>
    ["payout_bank_branches", bankId] as const,
};

// ==================== Domain Hooks ====================
// These hooks return domain entities

/**
 * Hook for fetching the connected bank account.
 * Returns BankAccount entity or null.
 */
export const useConnectedBankAccount = (uniqueId?: string, enabled = true) => {
  const repository = createBankAccountRepository();

  return useQuery<BankAccount | null, AxiosError<{message: string}>>({
    queryKey: BANK_ACCOUNT_QUERY_KEYS.connected(uniqueId),
    queryFn: () => repository.getConnectedBankAccount(),
    enabled,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
};

/**
 * Hook for validating a bank account.
 */
export const useBankAccountValidation = (params: ValidateBankParams, enabled = true) => {
  const repository = createBankAccountRepository();

  return useQuery<BankValidationResult, AxiosError<{message: string}>>({
    queryKey: BANK_ACCOUNT_QUERY_KEYS.validation(params.accountNumber),
    queryFn: () => repository.validateBankAccount(params),
    enabled: enabled && !!params.accountNumber && !!params.bankName,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook for connecting a new bank account.
 */
export const useConnectBankAccount = () => {
  const queryClient = useQueryClient();
  const repository = createBankAccountRepository();

  return useMutation<BankAccount, AxiosError<{message: string}>, ConnectBankParams>({
    mutationFn: (params) => repository.connectBankAccount(params),
    onError: (error) => {
      message.error(error.message);
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: BANK_ACCOUNT_QUERY_KEYS.connected()});
      // Also invalidate legacy key
      queryClient.invalidateQueries({queryKey: ["connected_bank"]});
      // Invalidate profile status so UI knows bank is now connected
      queryClient.invalidateQueries({queryKey: ["dashboard_status"]});
    },
  });
};

/**
 * Hook for updating an existing bank account.
 * Note: In v2, use useInitiateUpdateBankAccount + useCompleteUpdateBankAccount instead.
 */
export const useUpdateBankAccount = () => {
  const queryClient = useQueryClient();
  const repository = createBankAccountRepository();

  return useMutation<BankAccount, AxiosError<{message: string}>, UpdateBankParams>({
    mutationFn: (params) => repository.updateBankAccount(params),
    onError: (error) => {
      message.error(error.message);
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: BANK_ACCOUNT_QUERY_KEYS.connected()});
      // Also invalidate legacy key
      queryClient.invalidateQueries({queryKey: ["connected_bank"]});
      queryClient.invalidateQueries({queryKey: ["dashboard_status"]});
    },
  });
};

/**
 * Hook for initiating a bank account update (v2 two-step flow).
 * Step 1: Call this to send OTP to user's phone/email.
 * Step 2: Call useCompleteUpdateBankAccount with the OTP.
 */
export const useInitiateUpdateBankAccount = () => {
  return useMutation<void, AxiosError<{message: string}>, InitiateUpdateParams>({
    mutationFn: async (params) => {
      const repository = new BankAccountRepositoryV2();
      await repository.initiateUpdateBankAccount(params);
    },
    onError: (error) => {
      message.error(error.message);
    },
    onSuccess: () => {
      message.success("OTP sent to your registered phone/email");
    },
  });
};

/**
 * Hook for completing a bank account update (v2 two-step flow).
 * Called after user enters OTP received from useInitiateUpdateBankAccount.
 */
export const useCompleteUpdateBankAccount = () => {
  const queryClient = useQueryClient();

  return useMutation<BankAccount, AxiosError<{message: string}>, {otp: string}>({
    mutationFn: async ({otp}) => {
      const repository = new BankAccountRepositoryV2();
      return repository.completeUpdateBankAccount(otp);
    },
    onError: (error) => {
      message.error(error.message);
    },
    onSuccess: () => {
      message.success("Bank account updated successfully");
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: BANK_ACCOUNT_QUERY_KEYS.connected()});
      queryClient.invalidateQueries({queryKey: ["connected_bank"]});
      queryClient.invalidateQueries({queryKey: ["dashboard_status"]});
    },
  });
};

/**
 * Hook for fetching payout countries.
 */
export const usePayoutCountries = (enabled = true) => {
  const repository = createBankAccountRepository();

  return useQuery<PayoutCountry[], AxiosError<{message: string}>>({
    queryKey: BANK_ACCOUNT_QUERY_KEYS.countries(),
    queryFn: () => repository.getPayoutCountries(),
    enabled,
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });
};

/**
 * Hook for fetching banks in a country.
 */
export const useBanksByCountry = (countryCode: string | null, enabled = true) => {
  const repository = createBankAccountRepository();

  return useQuery<PayoutBank[], AxiosError<{message: string}>>({
    queryKey: BANK_ACCOUNT_QUERY_KEYS.banks(countryCode),
    queryFn: () => repository.getBanksByCountry(countryCode!),
    enabled: enabled && !!countryCode,
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });
};

/**
 * Hook for fetching bank branches.
 */
export const useBankBranches = (bankId: number | null, enabled = true) => {
  const repository = createBankAccountRepository();

  return useQuery<BankBranch[], AxiosError<{message: string}>>({
    queryKey: BANK_ACCOUNT_QUERY_KEYS.branches(bankId),
    queryFn: () => repository.getBankBranches(bankId!),
    enabled: enabled && !!bankId,
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });
};

/**
 * Mutation hook for validating bank account (imperative version).
 */
export const useVerifyBankAccountMutation = () => {
  const repository = createBankAccountRepository();

  return useMutation<
    BankValidationResult,
    AxiosError<{message: string}>,
    ValidateBankParams
  >({
    mutationFn: (params) => repository.validateBankAccount(params),
    onError: (error) => {
      message.error(error.message);
    },
  });
};

/**
 * Hook for invalidating bank account queries.
 */
export const useInvalidateBankAccounts = () => {
  const queryClient = useQueryClient();

  return {
    invalidateAll: () =>
      queryClient.invalidateQueries({queryKey: BANK_ACCOUNT_QUERY_KEYS.all}),
    invalidateConnected: () =>
      queryClient.invalidateQueries({queryKey: BANK_ACCOUNT_QUERY_KEYS.connected()}),
    invalidateCountries: () =>
      queryClient.invalidateQueries({queryKey: BANK_ACCOUNT_QUERY_KEYS.countries()}),
  };
};

// ==================== Legacy-Compatible Hooks ====================
// These hooks maintain backwards compatibility with existing components

/**
 * Legacy-compatible hook for fetching connected bank account.
 * Returns data in the old API response format with { bank: ... }.
 *
 * @deprecated Use useConnectedBankAccount for new code.
 */
export const useGetConnectedBankAccount = (options: {
  connectBankUniqueIdentifier: string;
  /** When false, the query does not run. Defaults to true for existing callers. */
  enabled?: boolean;
}) => {
  const repository = createBankAccountRepository();

  return useQuery<LegacyConnectedBankResponse, AxiosError<{message: string}>>({
    queryKey: LEGACY_QUERY_KEYS.connectedBank(options.connectBankUniqueIdentifier),
    queryFn: async () => {
      const bankAccount = await repository.getConnectedBankAccount();
      if (!bankAccount) {
        throw new Error(SYNTHETIC_NO_CONNECTED_BANK_MESSAGE);
      }
      return {bank: toLegacyBankResponse(bankAccount)};
    },
    enabled: options.enabled ?? true,
    onError: (error) => {
      if (isEmptyConnectedBankAccountMessage(error.message)) {
        return;
      }
      message.error(error.message);
    },
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
};

/**
 * Legacy-compatible hook for validating bank account.
 * Returns data in the old API response format.
 *
 * @deprecated Use useBankAccountValidation for new code.
 */
export const useValidateBankAccount = (payload: ValidateBankParams) => {
  const repository = createBankAccountRepository();

  return useQuery<LegacyValidateAccountResponse, AxiosError<{message: string}>>({
    queryKey: LEGACY_QUERY_KEYS.validateAccountNumber(payload.accountNumber),
    queryFn: async () => {
      const result = await repository.validateBankAccount(payload);
      return {name: result.accountName};
    },
    enabled: !!payload.accountNumber && !!payload.bankName,
    onError: (error) => {
      message.error(error.message);
    },
    refetchOnWindowFocus: false,
  });
};

/**
 * Legacy-compatible hook for validating bank account (mutation).
 *
 * @deprecated Use useVerifyBankAccountMutation for new code.
 */
export const useVerifyAccountNumberMutation = () => {
  const repository = createBankAccountRepository();

  return useMutation<
    {data: LegacyValidateAccountResponse},
    AxiosError<{message: string}>,
    ValidateBankParams
  >({
    mutationFn: async (params) => {
      const result = await repository.validateBankAccount(params);
      return {data: {name: result.accountName}};
    },
    onError: (error) => {
      message.error(error.message);
    },
  });
};

/**
 * Legacy-compatible hook for connecting bank account.
 *
 * @deprecated Use useConnectBankAccount for new code.
 */
export const useConnectBank = () => {
  const queryClient = useQueryClient();
  const repository = createBankAccountRepository();

  return useMutation<
    {data: LegacyConnectBankResponse},
    AxiosError<{message: string}>,
    ConnectBankParams
  >({
    mutationFn: async (params) => {
      const bankAccount = await repository.connectBankAccount(params);
      return {
        data: {
          bank: {
            userId: bankAccount.userId,
            country_name: bankAccount.countryName,
            country_code: bankAccount.countryCode,
            bank_name: bankAccount.bankName,
            bank_code: bankAccount.bankCode,
            account_number: bankAccount.accountNumber,
            account_name: bankAccount.accountName,
            provider: bankAccount.provider,
            id: bankAccount.id,
            createdAt: bankAccount.createdAt,
            updatedAt: bankAccount.updatedAt,
          },
        },
      };
    },
    onError: (error) => {
      message.error(error.message);
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: BANK_ACCOUNT_QUERY_KEYS.connected()});
      queryClient.invalidateQueries({queryKey: ["connected_bank"]});
      // Invalidate profile status so UI knows bank is now connected
      queryClient.invalidateQueries({queryKey: ["dashboard_status"]});
    },
  });
};

/**
 * Legacy-compatible hook for updating bank account.
 *
 * @deprecated Use useUpdateBankAccount for new code.
 */
export const useUpdateConnectedBank = () => {
  const queryClient = useQueryClient();
  const repository = createBankAccountRepository();

  return useMutation<
    {data: LegacyConnectBankResponse},
    AxiosError<{message: string}>,
    UpdateBankParams
  >({
    mutationFn: async (params) => {
      const bankAccount = await repository.updateBankAccount(params);
      return {
        data: {
          bank: {
            userId: bankAccount.userId,
            country_name: bankAccount.countryName,
            country_code: bankAccount.countryCode,
            bank_name: bankAccount.bankName,
            bank_code: bankAccount.bankCode,
            account_number: bankAccount.accountNumber,
            account_name: bankAccount.accountName,
            provider: bankAccount.provider,
            id: bankAccount.id,
            createdAt: bankAccount.createdAt,
            updatedAt: bankAccount.updatedAt,
          },
        },
      };
    },
    onError: (error) => {
      message.error(error.message);
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: BANK_ACCOUNT_QUERY_KEYS.connected()});
      queryClient.invalidateQueries({queryKey: ["connected_bank"]});
      queryClient.invalidateQueries({queryKey: ["dashboard_status"]});
    },
  });
};

/**
 * Legacy-compatible hook for fetching payout countries.
 *
 * @deprecated Use usePayoutCountries for new code.
 */
export const useGetPayoutCountries = () => {
  const repository = createBankAccountRepository();

  return useQuery<{data: {countries: PayoutCountry[]}}, AxiosError<{message: string}>>({
    queryKey: LEGACY_QUERY_KEYS.payoutCountries(),
    queryFn: async () => {
      const countries = await repository.getPayoutCountries();
      return {data: {countries}};
    },
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });
};

/**
 * Legacy-compatible hook for fetching banks by country (query version).
 *
 * @deprecated Use useBanksByCountry for new code.
 */
export const useGetPayoutBanksQuery = ({
  countryCode = null,
}: {
  countryCode: string | null;
}) => {
  const repository = createBankAccountRepository();

  return useQuery<{data: PayoutBank[]}, AxiosError<{message: string}>>({
    queryKey: LEGACY_QUERY_KEYS.payoutBanks(countryCode),
    queryFn: async () => {
      const banks = await repository.getBanksByCountry(countryCode!);
      return {data: banks};
    },
    enabled: !!countryCode,
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });
};

/**
 * Legacy-compatible hook for fetching banks by country (mutation version).
 *
 * @deprecated Use useBanksByCountry for new code.
 */
export const useGetPayoutBanks = () => {
  const repository = createBankAccountRepository();

  return useMutation<
    {data: PayoutBank[]},
    AxiosError<{message: string}>,
    {countryCode: string | null}
  >({
    mutationFn: async (params) => {
      const banks = await repository.getBanksByCountry(params.countryCode!);
      return {data: banks};
    },
    onError: (error) => {
      message.error(error.message);
    },
  });
};

/**
 * Legacy-compatible hook for fetching bank branches (query version).
 *
 * @deprecated Use useBankBranches for new code.
 */
export const useGetPayoutBankBranchesQuery = ({
  bankId = null,
}: {
  bankId: number | null;
}) => {
  const repository = createBankAccountRepository();

  return useQuery<
    {
      data: {
        branches: {
          data: Array<{
            id: number;
            branch_code: string;
            branch_name: string;
            swift_code: null;
            bic: null;
            bank_id: number;
          }>;
        } | null;
      };
    },
    AxiosError<{message: string}>
  >({
    queryKey: LEGACY_QUERY_KEYS.payoutBankBranches(bankId),
    queryFn: async () => {
      const branches = await repository.getBankBranches(bankId!);
      if (branches.length === 0) {
        return {data: {branches: null}};
      }
      return {
        data: {
          branches: {
            data: branches.map((b) => ({
              id: b.id,
              branch_code: b.branchCode,
              branch_name: b.branchName,
              swift_code: null,
              bic: null,
              bank_id: b.bankId,
            })),
          },
        },
      };
    },
    enabled: !!bankId,
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });
};

/**
 * Legacy-compatible hook for fetching bank branches (mutation version).
 *
 * @deprecated Use useBankBranches for new code.
 */
export const useGetBankBranches = () => {
  const repository = createBankAccountRepository();

  return useMutation<BankBranch[], AxiosError<{message: string}>, {bankCode: number}>({
    mutationFn: (params) => repository.getBankBranches(params.bankCode),
    onError: (error) => {
      message.error(error.message);
    },
  });
};
